import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { C } from '../../theme'

interface CloudLayoutProps {
  children: ReactNode
}

const NAV_LINKS = [
  { label: 'Home', href: '/' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Enterprise', href: '/enterprise' },
  { label: 'Demo', href: '/demo' },
  { label: 'Trust', href: '/trust' },
  { label: 'Docs', href: '/docs' },
]

const FOOTER_LINKS = {
  Product: [
    { label: 'Pricing', href: '/pricing' },
    { label: 'Trust & Security', href: '/trust' },
    { label: 'Documentation', href: '/docs' },
    { label: 'API Reference', href: '/docs/reference' },
  ],
  Company: [
    { label: 'Enterprise', href: '/enterprise' },
    { label: 'Contact', href: '/contact' },
    { label: 'Privacy Policy', href: '/legal' },
    { label: 'Terms of Service', href: '/legal' },
  ],
  Resources: [
    { label: 'Demo', href: '/demo' },
    { label: 'SDK', href: '/docs/sdk' },
    { label: 'Integration Guides', href: '/docs/guides' },
  ],
}

export function CloudLayout({ children }: CloudLayoutProps) {
  const location = useLocation()

  const isAdminRoute = location.pathname.startsWith('/admin')
  const isStandaloneRoute = location.pathname.startsWith('/v/')

  if (isAdminRoute || isStandaloneRoute) {
    return <>{children}</>
  }

  return (
    <div style={{ background: C.bg, color: C.text, minHeight: '100vh', fontFamily: C.sans, display: 'flex', flexDirection: 'column' }}>
      {/* ── Navbar ──────────────────────────────────────────── */}
      <nav style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        background: `${C.bg}ee`,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56 }}>
          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
            <img src="/netefy-logo.svg" alt="Netefy" style={{ height: 20, width: 'auto' }} />
            <span style={{ fontFamily: C.mono, fontSize: 14, fontWeight: 600, color: C.text }}>Netefy</span>
          </Link>

          {/* Desktop nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            {NAV_LINKS.map(({ label, href }) => (
              <Link key={label} to={href} style={{
                fontFamily: C.mono,
                fontSize: 13,
                color: location.pathname === href ? C.accent : C.muted,
                textDecoration: 'none',
                transition: 'color 0.15s',
              }}>
                {label}
              </Link>
            ))}
            <Link to="/contact" style={{
              display: 'inline-block',
              padding: '8px 20px',
              background: C.accent,
              color: C.bg,
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: C.mono,
              textDecoration: 'none',
            }}>
              Contact Sales
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Main ────────────────────────────────────────────── */}
      <main style={{ flex: 1 }}>
        {children}
      </main>

      {/* ── Footer ──────────────────────────────────────────── */}
      <footer style={{ borderTop: `1px solid ${C.border}`, padding: '48px 24px 32px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 40, marginBottom: 40 }}>
            {/* Brand column */}
            <div>
              <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', marginBottom: 12 }}>
                <img src="/netefy-logo.svg" alt="Netefy" style={{ height: 18, width: 'auto' }} />
                <span style={{ fontFamily: C.mono, fontSize: 13, fontWeight: 600, color: C.text }}>Netefy</span>
              </Link>
              <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, margin: 0, maxWidth: 280 }}>
                Developer-first identity verification. Botswana-first.
              </p>
            </div>

            {/* Link columns */}
            {Object.entries(FOOTER_LINKS).map(([section, links]) => (
              <div key={section}>
                <h4 style={{ fontFamily: C.mono, fontSize: 12, fontWeight: 600, color: C.accent, letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 16px' }}>
                  {section}
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {links.map(({ label, href }) => (
                    <Link key={label} to={href} style={{ fontSize: 13, color: C.muted, textDecoration: 'none', transition: 'color 0.15s' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = C.text }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = C.muted }}
                    >
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <p style={{ fontSize: 12, color: C.dim, margin: 0 }}>
              &copy; 2026 Netefy. All rights reserved.
            </p>
            <div style={{ display: 'flex', gap: 16 }}>
              <a href="https://github.com/Netefy/netefy" target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 12, color: C.dim, textDecoration: 'none', transition: 'color 0.15s' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = C.text }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = C.dim }}
              >
                GitHub
              </a>
              <a href="mailto:sales@netefy.app"
                style={{ fontSize: 12, color: C.dim, textDecoration: 'none', transition: 'color 0.15s' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = C.text }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = C.dim }}
              >
                sales@netefy.app
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
